import { Trash } from 'lucide-react';
import { ReactNode } from 'react';
import { toast } from 'sonner';
import { useAccountAPI } from '../../../api/use-account-api';
import { useData } from '../../../contexts/data-context/use-data';
import { Button } from '../../../design-system/button/button';
import { HorizontalScrollArea } from '../../../design-system/horizontal-scroll-area/horizontal-scroll-area';
import { Inline } from '../../../design-system/inline/inline';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { AccountSummaryCard } from './account-summary-card';
import { AllAccountsSummaryCard } from './all-accounts-summary-card';

const DEMO_DATA_FSP_ADAPTER = 'GOCARDLESS_DEMO_DATA';

type AccountSummarySectionProps = {
  accounts: AccountView[];
  selectedAccountId: string | null; // null to show all transactions and highlight the summary box
  baseCurrency: SupportedCountryCurrency;
  onAccountSelect: (id: string | null) => void;
  onResetSelectedAccount?: () => void;
};

export const AccountSummarySection = ({
  accounts,
  selectedAccountId, // null to show all transactions and highlight the summary box
  baseCurrency,
  onAccountSelect,
  onResetSelectedAccount,
}: AccountSummarySectionProps) => {
  const summaryData = {
    accountBalance: 0,
    availableBalance: 0,
    currency: baseCurrency,
  };

  accounts.forEach((account: AccountView) => {
    summaryData.accountBalance += account.baseCurrencyBalance || 0;
    summaryData.availableBalance += account.baseCurrencyAvailable || 0;
  });
  accounts.sort((a, b) => (b.closingBookedBalance ?? 0) - (a.closingBookedBalance ?? 0));

  return (
    <Inline space={3} wrap="nowrap" align="stretch" className="mb-2 w-max max-w-full">
      <AllAccountsSummaryCard
        currency={summaryData.currency}
        accountBalance={summaryData.accountBalance}
        onResetSelectedAccount={onResetSelectedAccount}
        isSelected={selectedAccountId === null}
      />

      <HorizontalScrollArea className="overflow-clip rounded-xl">
        {accounts.map((account) => {
          return (
            <AccountSummaryCardWrapper key={account.id} fspAdapter={account.fspAdapter} accountId={account.id}>
              <AccountSummaryCard
                key={account.id}
                id={account.id}
                bankName={account.institution?.name}
                bankLogo={account.institution?.logo}
                currency={account.currency}
                balance={account.balance}
                isSelected={selectedAccountId === account.id}
                onSelect={onAccountSelect}
              />
            </AccountSummaryCardWrapper>
          );
        })}
      </HorizontalScrollArea>
    </Inline>
  );
};

type AccountSummaryCardWrapperProps = {
  children: ReactNode;
  fspAdapter?: string | null;
  accountId: string;
};

const AccountSummaryCardWrapper = ({ children, fspAdapter, accountId }: AccountSummaryCardWrapperProps) => {
  const { deleteAccount } = useAccountAPI();
  const { mutations } = useData();

  const isDemoData = fspAdapter === DEMO_DATA_FSP_ADAPTER;

  return (
    <div className="group relative last:mr-5">
      {isDemoData && (
        <div className="absolute bottom-2 right-2 z-10 rounded-lg border-2 border-transparent bg-seapoint-yellow/80 px-2 py-1 text-right">
          <Inline justify="between" align="center" space={1}>
            <p className="text-[10px] font-medium">Demo</p>

            <Button
              variant="ghost"
              size="icon"
              className="h-3 w-3"
              onClick={async () => {
                try {
                  await deleteAccount(accountId);

                  await mutations.mutateAll();

                  toast.success('Demo account deleted');
                } catch {
                  toast.error('There was a problem deleting the demo account');
                }
              }}
            >
              <Trash className="h-full w-full" />
            </Button>
          </Inline>
        </div>
      )}

      {children}
    </div>
  );
};
